import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Form,
  DatePicker,
  Select,
  Button,
  Typography,
  ColorPicker,
  Tooltip,
  Alert,
} from 'antd';
import cn from 'classnames';
import moment from 'moment';

import { CustomInputNumber } from '@app/components/CustomInputNumber';
import { useEmployee } from '@app/components/RecordModal/components/RecordDetails/hooks';
import type {
  EmployeeType,
  RecordDetailsForm,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { TagSelect } from '@app/components/TagsSelect';
import { DATE_PICKER_FORMAT, DEFAULT_COLOR, TAGS_COLORS } from '@app/constants';
import { withTestId } from '@app/utils';

import '@app/components/RecordModal/components/RecordDetails/styles/Employee.desktop.scss';
import '@app/components/RecordModal/components/RecordDetails/styles/Employee.tablet.scss';
import '@app/components/RecordModal/components/RecordDetails/styles/Employee.mobile.scss';

const { Text } = Typography;
const { Item } = Form;
const PF = 'employee';
const INVALID_TIME_RANGE_MESSAGE =
  'Время окончания не может быть раньше начала';
const INVALID_DURATION_MESSAGE = 'Длительность не может быть меньше 15 минут';
export const Employee: EmployeeType = ({
  mainEmployeeOptions,
  tagOptions,
  toggleLock,
  disabled,
  showDoubleRecordSelects,
  showDoubleRecordButton,
  handleAddAdditionalEmployee,
  handleRemoveAdditionalEmployee,
  handleAdditionalEmployeeChange,
  handleDuplicateClick,
  additinalEmployeeOptions,
  additionalEmployees,
  mainEmployeeSelected,
  onChangeTags,
  showEmployeePayoff,
  isFormDisabled,
  onSelectEmployee,
  isFetchingGetClientServicesByEmployeeId,
  isLoadingGetClientServicesByEmployeeId,
}) => {
  const form = Form.useFormInstance<RecordDetailsForm>();

  const {
    timeOptions,
    hourOptions,
    minuteOptions,
    LockIcon,
    lockedEmployee,
    disabledEmployee,
    setFieldValue,
    handleChangeTimes,
    handleChangeDate,
    isLoading,
    isOpen,
    warnings,
  } = useEmployee({
    disabled,
    isFetchingGetClientServicesByEmployeeId,
    isLoadingGetClientServicesByEmployeeId,
  });

  return (
    <div className={PF}>
      <div className={`${PF}__details`}>
        <div className={`${PF}__row`}>
          <div className={`${PF}__label`}>
            <Text className="label-require" data-testid="date-time-label">
              Дата и время
            </Text>
          </div>
          <div className={`${PF}__fields`}>
            <Form.Item
              shouldUpdate={(prev, curr) => {
                if (!isOpen || isLoading) {
                  return false;
                }
                return (
                  prev.Date !== curr.Date ||
                  prev.TimeStart !== curr.TimeStart ||
                  prev.TimeEnd !== curr.TimeEnd
                );
              }}
              className={`${PF}__fields-combined`}
              noStyle
            >
              {({ getFieldValue }) => {
                const date = getFieldValue('Date');
                const timeStart = getFieldValue('TimeStart');
                const timeEnd = getFieldValue('TimeEnd');

                const errors: string[] = [];

                if (!isOpen || isLoading) {
                  return false;
                }

                if (!date && !isLoading) errors.push('Выберите дату');
                if (!timeStart && !isLoading)
                  errors.push('Выберите время начала');
                if (!timeEnd && !isLoading)
                  errors.push('Выберите время окончания');

                const timeStartMoment = moment(timeStart, 'HH:mm');
                const timeEndMoment = moment(timeEnd, 'HH:mm');

                if (
                  timeStart &&
                  timeEnd &&
                  timeEndMoment.isSameOrBefore(timeStartMoment) &&
                  !isLoading
                ) {
                  errors.push(INVALID_TIME_RANGE_MESSAGE);
                }

                return (
                  <Form.Item
                    validateStatus={
                      errors.length > 0 && !isLoading && isOpen
                        ? 'error'
                        : undefined
                    }
                    help={
                      errors.length > 0 && !isLoading && isOpen
                        ? errors.join('. ')
                        : null
                    }
                    required
                    className={`${PF}__fields-combined-inner`}
                  >
                    <div className={`${PF}__fields`}>
                      <Form.Item
                        name="Date"
                        noStyle
                        rules={[{ required: true, message: 'Выберите дату' }]}
                      >
                        <DatePicker
                          data-testid="date-time"
                          className={`${PF}__fields-date-picker`}
                          size="large"
                          inputReadOnly
                          format={DATE_PICKER_FORMAT}
                          disabled={disabled}
                        />
                      </Form.Item>
                      <div className={`${PF}__fields-range`}>
                        <Form.Item
                          name="TimeStart"
                          noStyle
                          rules={[
                            {
                              required: true,
                              message: 'Выберите время начала',
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            data-testid="start-time"
                            options={timeOptions}
                            size="large"
                            disabled={disabled}
                            onChange={handleChangeTimes('TimeStart')}
                            popupRender={withTestId('start-time-dropdown')}
                            placeholder="Начало"
                          />
                        </Form.Item>
                        <Text>-</Text>
                        <Form.Item
                          name="TimeEnd"
                          noStyle
                          rules={[
                            {
                              required: true,
                              message: 'Выберите время окончания',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const start = getFieldValue('TimeStart');
                                if (!start || !value) return Promise.resolve();

                                const startMoment = moment(start, 'HH:mm');
                                const endMoment = moment(value, 'HH:mm');

                                if (endMoment.isSameOrBefore(startMoment)) {
                                  return Promise.reject(
                                    new Error(INVALID_TIME_RANGE_MESSAGE)
                                  );
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Select
                            showSearch
                            data-testid="end-time"
                            options={timeOptions}
                            size="large"
                            disabled={disabled}
                            onChange={handleChangeTimes('TimeEnd')}
                            popupRender={withTestId('end-time-dropdown')}
                            placeholder="Окончание"
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </div>

        <div className={`${PF}__row`}>
          <div data-testid="duration" className={`${PF}__label`}>
            Длительность
          </div>
          <div className={`${PF}__fields`}>
            <Form.Item
              shouldUpdate={(prev, curr) => {
                if (!isOpen || isLoading) {
                  return false;
                }
                return (
                  prev.DurationHour !== curr.DurationHour ||
                  prev.DurationMinute !== curr.DurationMinute
                );
              }}
              className={`${PF}__fields-duration-combined`}
            >
              {({ getFieldValue }) => {
                const hour = getFieldValue('DurationHour') ?? 0;
                const minute = getFieldValue('DurationMinute') ?? 0;
                const totalMinutes = hour * 60 + minute;

                return (
                  <Form.Item
                    className={`${PF}__fields-duration-combined-inner`}
                    validateStatus={
                      totalMinutes < 15 && !isLoading && isOpen
                        ? 'error'
                        : undefined
                    }
                    help={
                      totalMinutes < 15 && !isLoading && isOpen
                        ? INVALID_DURATION_MESSAGE
                        : null
                    }
                    required
                  >
                    <div className={`${PF}__fields`}>
                      <Form.Item
                        name="DurationHour"
                        noStyle
                        rules={[{ required: true }]}
                      >
                        <Select
                          className={`${PF}__fields-duration-select`}
                          placeholder="Часы"
                          size="large"
                          disabled={disabled}
                          options={hourOptions}
                          onChange={handleChangeDate}
                          data-testid="duration-hours"
                          popupRender={withTestId('duration-hours-dropdown')}
                        />
                      </Form.Item>
                      <Form.Item
                        name="DurationMinute"
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: 'Выберите минуты',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const hour = getFieldValue('DurationHour') ?? 0;
                              const minute = value ?? 0;
                              const total = hour * 60 + minute;
                              if (total < 15) {
                                return Promise.reject(
                                  new Error(INVALID_DURATION_MESSAGE)
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select
                          className={`${PF}__fields-duration-select`}
                          placeholder="Минуты"
                          size="large"
                          disabled={disabled}
                          options={minuteOptions}
                          onChange={handleChangeDate}
                          data-testid="duration-minutes"
                          popupRender={withTestId('duration-minutes-dropdown')}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </div>

        {/* ТЗ us/11144 убрало только отдельную кнопку «Присвоить тег» (окно
            пресетов): её заменило окно тегов после создания записи. Само поле
            тегов остаётся — в нём видны теги, выбранные в окне тегов, и их
            можно поправить (issue/11332). */}
        <div className={`${PF}__row`}>
          <div className={`${PF}__label`} data-testid="assign-tag">
            Присвоить тег
          </div>
          <div className={`${PF}__fields`}>
            <Item name="Tags" className={`${PF}__fields-tags`}>
              <TagSelect
                disabled={disabled}
                className={`${PF}__fields-tags-select`}
                placeholder="Выберите теги"
                size="large"
                showSearch
                options={tagOptions}
                dataTestId="tag-select"
                dropdownDataTestId="tag-dropdown"
                tagTestId="record-tag"
                onChange={onChangeTags}
                allowClear={false}
              />
            </Item>
          </div>
        </div>

        <div className={`${PF}__row ${PF}__row-color`}>
          <div className={`${PF}__label`}>
            <Item>
              <span data-testid="color-label">Цвет записи</span>
            </Item>
          </div>
          <div className={`${PF}__fields`}>
            <Item name="Color">
              <ColorPicker
                data-testid="color-select"
                disabled={disabled}
                onChange={(_, hex) => {
                  setFieldValue('Color', hex);
                }}
                presets={[
                  { label: '', colors: TAGS_COLORS },
                  { label: '', colors: [DEFAULT_COLOR] },
                ]}
                panelRender={withTestId('color-list')}
              />
            </Item>
          </div>
        </div>

        <div className={`${PF}__row`}>
          <div
            className={`${PF}__label label-require`}
            data-testid="performer-label"
          >
            Исполнитель
          </div>
          <div className={`${PF}__fields`}>
            <Item
              name="Employee"
              rules={[{ required: true, message: 'Выберите исполнителя' }]}
              className={`${PF}__fields-employee`}
            >
              <Select
                data-testid="performer-select"
                placeholder="Выберите исполнителя"
                options={mainEmployeeOptions}
                onSelect={onSelectEmployee}
                size="large"
                className={`${PF}__fields-employee-select`}
                disabled={disabledEmployee}
                optionFilterProp="label"
                showSearch
                loading={disabledEmployee && !lockedEmployee}
                popupRender={withTestId('performer-dropdown')}
              />
            </Item>
            <div className={`${PF}__lock`}>
              <Item name="IsLocked" valuePropName="checked">
                <Button
                  type={lockedEmployee ? 'primary' : 'default'}
                  size="large"
                  className={`${PF}__lock-button`}
                  onClick={toggleLock}
                  disabled={disabled}
                >
                  <LockIcon data-testid="assign-performer" />
                </Button>
              </Item>
            </div>
          </div>
        </div>
        {showDoubleRecordButton && (
          <Item className="full-width">
            <div className={`${PF}__row`}>
              <div className={`${PF}__label`} />
              <div className={`${PF}__fields full-width`}>
                <Button
                  size="large"
                  className="full-width"
                  onClick={handleDuplicateClick}
                  disabled={disabled}
                  data-testid="double-record"
                >
                  Дублировать запись
                </Button>

                <Tooltip title="Вы можете дублировать запись на других исполнителей. Услуги не будут дублироваться.">
                  <InfoCircleOutlined className={`${PF}__double-record-info`} />
                </Tooltip>
              </div>
            </div>
          </Item>
        )}
        {showDoubleRecordSelects && (
          <div className={`${PF}__row`}>
            <div className={`${PF}__label gap-4`}>
              <Text>Дублировать для</Text>
              <Tooltip title="Вы можете дублировать запись на других исполнителей. Услуги не будут дублироваться.">
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <div className={`${PF}__fields ${PF}__additional-employees`}>
              {additionalEmployees.map((emp, idx) => {
                const additionalSelectedExcludingCurrent =
                  additionalEmployees.filter((v, i) => i !== idx && v !== null);
                const filteredAdditionalOptions =
                  additinalEmployeeOptions.filter(
                    (opt) =>
                      opt.value === emp ||
                      (opt.value !== mainEmployeeSelected &&
                        !additionalSelectedExcludingCurrent.includes(opt.value))
                  );

                return (
                  <div key={idx} className={`${PF}__additional-employees-row`}>
                    <Select
                      className={cn(`${PF}__additional-employees-select`, {
                        [`${PF}__additional-employees-select-two-buttons`]:
                          idx === additionalEmployees.length - 1,
                      })}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      placeholder="Выберите исполнителя"
                      options={filteredAdditionalOptions}
                      value={emp ?? undefined}
                      onChange={(value: number) =>
                        handleAdditionalEmployeeChange(idx, value)
                      }
                      popupRender={withTestId('performer-dropdown')}
                    />
                    {idx === additionalEmployees.length - 1 && (
                      <Button
                        type="primary"
                        size="large"
                        onClick={handleAddAdditionalEmployee}
                        disabled={
                          !emp ||
                          filteredAdditionalOptions.length === 1 ||
                          disabled
                        }
                      >
                        +
                      </Button>
                    )}
                    <Button
                      size="large"
                      type="default"
                      onClick={() => handleRemoveAdditionalEmployee(idx)}
                      disabled={disabled}
                    >
                      -
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {showEmployeePayoff && (
          <div className={`${PF}__row`}>
            <div className={`${PF}__label`}>Заработная плата</div>
            <div className={`${PF}__fields`}>
              <Item name="EmployeePayoff" className={`${PF}__fields-employee`}>
                <CustomInputNumber
                  inputValue={form.getFieldValue('EmployeePayoff')}
                  isAllowed={(floatValue: number) => floatValue >= 0.01}
                  onSuccess={({ value }) => {
                    form.setFieldValue('EmployeePayoff', Number(value));
                  }}
                  disabled={disabled}
                />
              </Item>
            </div>
          </div>
        )}
      </div>
      {warnings.length > 0 && (
        <div className={`${PF}__warning`}>
          {warnings.map(({ Message }, index) => (
            <Alert
              key={index}
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              message={Message}
            />
          ))}
        </div>
      )}
      {isFormDisabled && (
        <div className={`${PF}__warning`}>
          <Alert
            type="warning"
            showIcon
            icon={<InfoCircleOutlined />}
            message="У вас нет прав на изменение оплаченной записи"
          />
        </div>
      )}
    </div>
  );
};
